import { PointerEvent } from 'react';

import { isNull } from 'lodash';
import { Editor, Element as SlateElement, Transforms } from 'slate';

import {
  LIST_TYPES,
  MAX_INDENT_LEVEL,
  TEXT_ALIGN_TYPES,
} from './EditorTextArea.config';
import type {
  AlignType,
  CustomEditor,
  CustomElement,
  CustomElementFormat,
  CustomElementWithAlign,
  CustomTextKey,
  ListType,
} from './EditorTextArea.types';

export const escapeSpecialCharacters = (text: string) => {
  if (!text) return text;

  return (
    text
      // Normalize accented characters (é → e)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')

      // Smart quotes → straight quotes
      .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F]/g, '"')

      // Dashes & hyphens
      .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-')

      // Ellipsis
      .replace(/\u2026/g, '...')

      // Non-breaking space → normal space
      .replace(/\u00A0/g, ' ')
      // Windows CRLF -> LF
      .replace(/\r\n/g, '\n')
      // Old Mac CR -> LF
      .replace(/\r/g, '\n')
      // Remove control chars like ^Y
      // eslint-disable-next-line no-control-regex
      .replace(/\x19/g, '')
      // Remove ASCII control chars except newline/tab
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x08\x0B-\x1F\x7F]/g, '')
      .split('')
      .filter(character => character.charCodeAt(0) <= 0x7f)
      .join('')
  );
};

export const containsSpecialCharacters = (text: string) =>
  escapeSpecialCharacters(text) !== text;

export const onPointerDown = (event: PointerEvent<HTMLButtonElement>) =>
  event.preventDefault();

export const withInlines = (editor: CustomEditor) => {
  const { isInline } = editor;

  editor.isInline = element =>
    ['link'].includes(element.type) || isInline(element);

  return editor;
};

export const isMarkActive = (editor: CustomEditor, format: CustomTextKey) => {
  try {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  } catch {
    return false;
  }
};

export const isAlignType = (format: CustomElementFormat): format is AlignType =>
  TEXT_ALIGN_TYPES.includes(format as AlignType);

export const isListType = (format: CustomElementFormat): format is ListType =>
  LIST_TYPES.includes(format as ListType);

export const isAlignElement = (
  element: CustomElement,
): element is CustomElementWithAlign =>
  'align' in element && element.align !== undefined;

export const isBlockActive = (
  editor: CustomEditor,
  format: CustomElementFormat,
  blockType: 'type' | 'align' = 'type',
) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          const element = n as CustomElement;
          if (blockType === 'align' && isAlignElement(element)) {
            return element.align === format;
          }
          return element.type === format;
        }
        return false;
      },
    }),
  );

  return !!match;
};

export const isIndentable = (editor: CustomEditor) => {
  if (!isBlockActive(editor, 'ol') && !isBlockActive(editor, 'ul')) {
    return false;
  }

  const { selection } = editor;

  const [listItemEntry] = Editor.nodes(editor, {
    match: n => SlateElement.isElement(n) && n.type === 'li',
    at: selection ?? undefined,
  });

  if (listItemEntry) {
    const [, listItemPath] = listItemEntry;
    const listItemIndex = listItemPath[listItemPath.length - 1];

    if (listItemIndex === 0) {
      return false;
    }
  }

  return true;
};

export const isOutdentable = (editor: CustomEditor) => {
  if (!isBlockActive(editor, 'ol') && !isBlockActive(editor, 'ul')) {
    return false;
  }

  const { selection } = editor;
  if (!selection) return false;

  const ancestors = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: n => !Editor.isEditor(n) && SlateElement.isElement(n),
    }),
  );

  const liCount = ancestors
    .map(([node]) =>
      SlateElement.isElement(node) ? (node as CustomElement).type : null,
    )
    .filter(type => type === 'li').length;

  return liCount >= 2;
};

export const isLinkActive = (editor: CustomEditor) => {
  const [match] = Array.from(
    Editor.nodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement).type === 'link',
    }),
  );
  return !!match;
};

export const toggleMark = (editor: CustomEditor, format: CustomTextKey) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

export const toggleBlock = (
  editor: CustomEditor,
  format: CustomElementFormat,
) => {
  const isActive = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );
  const isList = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: n => {
      if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
        const element = n as CustomElement;
        return isListType(element.type) && !isAlignType(format);
      }
      return false;
    },
    split: true,
  });

  let newProperties: Partial<CustomElement>;

  if (isAlignType(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    } as Partial<CustomElement>;
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'li' : format,
    } as Partial<CustomElement>;
  }

  Transforms.setNodes<CustomElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const unwrapLink = (editor: CustomEditor) => {
  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (n as CustomElement).type === 'link',
  });
};

export const insertLink = (
  editor: CustomEditor,
  url: string,
  title?: string,
) => {
  if (!url) return;

  unwrapLink(editor);

  const linkNode = {
    type: 'link',
    url,
    children: [{ text: title ?? url }],
  } as CustomElement;

  const { selection } = editor;

  try {
    if (
      !isNull(selection) &&
      !(
        selection.anchor.path === selection.focus.path &&
        selection.anchor.offset === selection.focus.offset
      )
    ) {
      // wrap the selected nodes with the link node
      Transforms.wrapNodes(editor, linkNode as SlateElement, { split: true });
      Transforms.collapse(editor, { edge: 'end' });
    } else {
      // insert a new link node at the cursor
      Transforms.insertNodes(editor, linkNode as SlateElement, {
        mode: 'highest',
        voids: true,
      });
      Transforms.collapse(editor, { edge: 'end' });
    }
  } catch {
    Transforms.insertText(editor, url);
  }
};

export const toggleParagraphIndent = (
  editor: CustomEditor,
  direction: 'indent' | 'outdent',
) => {
  const { selection } = editor;
  if (!selection) return;

  const blocks = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: n => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          const element = n as CustomElement;

          return (
            element.type !== 'li' && element.type !== 'list-content-wrapper'
          );
        }

        return false;
      },
    }),
  );

  if (blocks.length === 0) return;

  Editor.withoutNormalizing(editor, () => {
    blocks.forEach(([node, path]) => {
      const element = node as CustomElement & { indent?: number };
      const currentIndent = element.indent || 0;

      let newIndent: number | undefined;
      if (direction === 'indent') {
        newIndent = Math.min(currentIndent + 1, MAX_INDENT_LEVEL);
      } else {
        newIndent = Math.max(currentIndent - 1, 0);
        if (newIndent === 0) newIndent = undefined;
      }

      Transforms.setNodes(editor, { indent: newIndent }, { at: path });
    });
  });
};

export const toggleIndent = (
  editor: CustomEditor,
  direction: 'indent' | 'outdent',
) => {
  const { selection } = editor;
  if (!selection) return;

  // Find the current list item at the cursor position
  const [listItemMatch] = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as CustomElement).type === 'li',
      mode: 'lowest',
    }),
  );

  if (!listItemMatch) {
    toggleParagraphIndent(editor, direction);
    return;
  }

  const [, listItemPath] = listItemMatch;

  // Find the parent list
  const [parentListMatch] = Array.from(
    Editor.nodes(editor, {
      at: listItemPath,
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        isListType((n as CustomElement).type),
    }),
  );

  if (!parentListMatch) {
    toggleParagraphIndent(editor, direction);
    return;
  }

  const [parentList] = parentListMatch;
  const listType = (parentList as CustomElement).type;

  if (direction === 'indent') {
    // Check if there's a previous sibling
    const previousIndex = listItemPath[listItemPath.length - 1];
    if (previousIndex === 0) return; // Can't indent first item

    const previousPath = [...listItemPath.slice(0, -1), previousIndex - 1];

    Editor.withoutNormalizing(editor, () => {
      try {
        const [listItemNode] = Editor.node(editor, listItemPath);
        const [previousNode] = Editor.node(editor, previousPath);

        if (!SlateElement.isElement(previousNode)) return;

        const previousElement = previousNode as CustomElement;

        // Check if previous sibling already has a nested list as last child
        const lastChild =
          previousElement.children[previousElement.children.length - 1];

        if (
          lastChild &&
          SlateElement.isElement(lastChild) &&
          isListType((lastChild as CustomElement).type)
        ) {
          // Insert into existing nested list
          const lastChildElement = lastChild as CustomElement;
          const newPath = [
            ...previousPath,
            previousElement.children.length - 1,
            (lastChildElement.children as Array<unknown>).length,
          ];
          Transforms.removeNodes(editor, { at: listItemPath });
          Transforms.insertNodes(editor, listItemNode as SlateElement, {
            at: newPath,
          });
        } else {
          Transforms.removeNodes(editor, { at: listItemPath });
          Transforms.removeNodes(editor, { at: previousPath });
          Transforms.insertNodes(
            editor,
            {
              type: 'li',
              children: [
                {
                  ...previousNode,
                  type: 'list-content-wrapper',
                },
                {
                  type: listType,
                  children: [listItemNode],
                },
              ],
            } as SlateElement,
            { at: previousPath },
          );
        }
      } catch {
        // Indent operation failed
      }
    });
  } else {
    // Outdent
    // Check if we're in a nested list (path length > 2)
    if (listItemPath.length <= 2) return;

    try {
      const [listItemNode] = Editor.node(editor, listItemPath);

      // Find the grandparent list path
      const grandparentListPath = listItemPath.slice(0, -2);

      // Calculate the new position (after the parent list item)
      const parentItemIndex = listItemPath[listItemPath.length - 3];
      const newPath = [...grandparentListPath, parentItemIndex + 1];

      // Remove and insert the item out of the nested list
      Transforms.removeNodes(editor, { at: listItemPath });
      Transforms.insertNodes(editor, listItemNode as SlateElement, {
        at: newPath,
      });

      // Clean up empty lists
      const parentListPath = listItemPath.slice(0, -1);
      try {
        const [parentListNode] = Editor.node(editor, parentListPath);
        const parentListElement = parentListNode as CustomElement;
        if (
          SlateElement.isElement(parentListNode) &&
          (parentListElement.children as Array<unknown>).length === 0
        ) {
          Transforms.removeNodes(editor, {
            at: parentListPath,
          });
        }
      } catch {
        // Parent list might already be removed
      }
    } catch {
      // Outdent operation failed
    }
  }
};
